import { LitElement, html, css, nothing } from 'https://cdn.jsdelivr.net/npm/lit@3.1.2/+esm';

class ProductColorSwatch extends LitElement {
  static properties = {
    props: { type: Object },
    relatedProducts: { state: true },
  };

  static styles = css`
    .relatedProducts {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--gap-2xs);
    }
    .swatch-link { display: inline-block;
     text-decoration: none;
     line-height: 0;
     padding: 4px;
    }
    .swatch {
      width: 24px; 
      height: 24px;
      border-radius: 50%;
      outline: 1px solid var(--color-border);
      outline-offset: 0;
    }
    .swatch[aria-current="true"], .swatch:hover {
      outline: 1px solid var(--color-foreground);
      outline-offset: 2px;
    }
    .legend-title {
    font-weight: 500}
  `;

  constructor() {
    super();
    this.props = {};
    this.relatedProducts = [];
    this.hasLoaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (typeof this.props === 'string') {
      try { this.props = JSON.parse(this.props); } catch {}
    }
    this.fetchRelatedProducts();
  }

  static extractSwatchHex(tags = []) {
    const swatchTag = tags.find(t => /^swatch\s*:/.test(String(t).toLowerCase()));
    if (!swatchTag) return null;
    const match = String(swatchTag).match(/#([0-9a-fA-F]{3,6})\b/);
    return match ? `#${match[1]}` : null;
  }

  async fetchRelatedProducts() {
    const { currentProductId, currentProductStyleTag, storefrontAccessKey } = this.props || {};
    if (!currentProductId || !currentProductStyleTag || !storefrontAccessKey) return;

    const options = {
      method: 'POST',
      headers: new Headers({
        'content-type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessKey,
      }),
      body: JSON.stringify({
        query: `{
          products(first: 120, query: "tag:'${currentProductStyleTag}'") {
            nodes { id handle title onlineStoreUrl tags }
          }
        }`,
      }),
    };

    try {
      const res = await fetch('/api/2023-10/graphql.json', options);
      const data = await res.json();
      const nodes = data?.data?.products?.nodes || [];

      const withHex = nodes
        .map(p => ({ ...p, __swatchHex: ProductColorSwatch.extractSwatchHex(p.tags) }))
        .filter(p => !!p.__swatchHex);

      const ordered = withHex.sort((node) => {
        if (node?.id?.replace('gid://shopify/Product/', '') === this.props.currentProductId) return -1;
        return 0;
      });

      this.relatedProducts = ordered;
    } catch (err) {
      console.error('Failed to fetch related products', err);
      this.relatedProducts = [];
    } finally {
      this.hasLoaded = true;
      this.requestUpdate();
    }
  }

  // Toggle the native hidden attribute based on how many swatches we have
  updated(changed) {
    const count = this.relatedProducts?.length || 0;
    if (!this.hasLoaded) {
      // Avoid CLS during load; keep space reserved.
      this.style.visibility = 'hidden';
      this.style.display = '';
      return;
    }

    if (count > 1) {
      this.style.display = '';
      this.style.visibility = 'visible';
    } else {
      this.style.display = 'none';
      this.style.visibility = 'hidden';
    }
  }

  render() {
    if (!(this.relatedProducts?.length > 1)) return nothing;

    const colorName = this.props?.currentProductColorName;

    return html`
      ${colorName?.length
        ? html`<legend style="margin-block-end: var(--margin-xs);padding: 0;"><span class="legend-title">Color:</span> ${colorName}</legend>`
        : nothing}
      <div class="relatedProducts">
        ${this.relatedProducts.map((p) => {
          const hex = p.__swatchHex;
          const isCurrent = p.id?.replace('gid://shopify/Product/', '') === this.props.currentProductId;
          return html`
            <a class="swatch-link" href="${p.onlineStoreUrl}" aria-label="${p.title} ${hex}">
              <div
                class="swatch"
                style="background-color:${hex}"
                role="img"
                aria-current="${isCurrent ? 'true' : 'false'}"
                aria-label="${p.title} swatch ${hex}"
                title="${p.title} ${hex}"
              ></div>
            </a>
          `;
        })}
      </div>
    `;
  }
}
try {
  if (!customElements.get('product-color-swatches')) {
    customElements.define('product-color-swatches', ProductColorSwatch);
  }
} catch (err) {
  if (err?.name !== 'NotSupportedError') throw err;
}
